import React, { useEffect } from "react";
import { useSnackbar } from "notistack";
import { useNavigate } from "react-router-dom";
import { Box, Typography } from "@mui/material";
import { connect } from "trim-redux";
import { readAdminForegroundOrderNotificationsEnabled } from "../../../config/adminOrderNotificationsPref";
import {
  NEW_MESSAGE_SOUND,
  NEW_ORDER_SOUND,
} from "../../../assets/sounds/notificationSound";

const MIN_MS_BETWEEN_NOTIFICATION_SOUND_STARTS = 1 * 10 * 1000; // 10 seconds

const SILENT_WAV =
  "data:audio/wav;base64,UklGRigAAABXQVZFZm10IBIAAAABAAEARKwAAIhYAQACABAAAABkYXRhAgAAAAE=";

let audioUnlocked = false;
const pendingSoundSrcWhileLocked = [];

let lastNotificationSoundStartTime = 0;

const soundRequestQueue = [];
let soundDrainRunning = false;

let sharedNotificationAudio = null;

function getSharedNotificationAudio() {
  if (typeof Audio === "undefined") {
    return null;
  }
  if (!sharedNotificationAudio) {
    sharedNotificationAudio = new Audio();
    sharedNotificationAudio.preload = "auto";
  }
  return sharedNotificationAudio;
}

function resolveNotificationSoundSrc(payload) {
  const raw = String(payload?.notification?.title || payload?.data?.title || "").trim();
  const lower =
    typeof raw.toLocaleLowerCase === "function" ?
      raw.toLocaleLowerCase("tr-TR")
    : raw.toLowerCase();
  const asciiFold = lower
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/ı/g, "i")
    .replace(/ş/g, "s")
    .replace(/ğ/g, "g")
    .replace(/ü/g, "u")
    .replace(/ö/g, "o")
    .replace(/ç/g, "c");
  if (
    lower.includes("sipariş") ||
    lower.includes("siparis") ||
    asciiFold.includes("siparis") ||
    asciiFold.includes("yeni siparis")
  ) {
    return NEW_ORDER_SOUND;
  }
  return NEW_MESSAGE_SOUND;
}

function isOrderLikeForegroundFcmPayload(payload) {
  return resolveNotificationSoundSrc(payload) === NEW_ORDER_SOUND;
}

function playOneNotificationSoundUntilDone(src) {
  return new Promise((resolve) => {
    let settled = false;
    const finish = () => {
      if (settled) {
        return;
      }
      settled = true;
      resolve();
    };

    const audio = getSharedNotificationAudio();
    if (!audio) {
      finish();
      return;
    }

    const onEnded = () => finish();
    const onError = () => finish();

    audio.removeEventListener("ended", onEnded);
    audio.removeEventListener("error", onError);
    audio.addEventListener("ended", onEnded, { once: true });
    audio.addEventListener("error", onError, { once: true });

    try {
      audio.src = src;
      audio.volume = 0.85;
      const p = audio.play();
      if (p !== undefined) {
        void p.catch((err) => {
          if (process.env.NODE_ENV === "development") {
            console.warn("FCM notification sound play rejected:", err);
          }
          finish();
        });
      }
      setTimeout(finish, 45_000);
    } catch (e) {
      if (process.env.NODE_ENV === "development") {
        console.warn("notification sound error", e);
      }
      finish();
    }
  });
}

async function runNotificationSoundDrain() {
  if (soundDrainRunning) {
    return;
  }
  soundDrainRunning = true;
  try {
    while (soundRequestQueue.length > 0) {
      const src = soundRequestQueue.shift();
      if (!src) {
        continue;
      }
      if (lastNotificationSoundStartTime > 0) {
        const nextEarliest =
          lastNotificationSoundStartTime +
          MIN_MS_BETWEEN_NOTIFICATION_SOUND_STARTS;
        const waitMs = nextEarliest - Date.now();
        if (waitMs > 0) {
          await new Promise((r) => setTimeout(r, waitMs));
        }
      }
      lastNotificationSoundStartTime = Date.now();
      await playOneNotificationSoundUntilDone(src);
    }
  } finally {
    soundDrainRunning = false;
    if (soundRequestQueue.length > 0) {
      void runNotificationSoundDrain();
    }
  }
}

function enqueueNotificationSoundPlayback(payload) {
  const src = resolveNotificationSoundSrc(payload);
  if (!audioUnlocked) {
    pendingSoundSrcWhileLocked.push(src);
    return;
  }
  soundRequestQueue.push(src);
  void runNotificationSoundDrain();
}

function flushPendingSoundsAfterUnlock() {
  if (!audioUnlocked) {
    return;
  }
  while (pendingSoundSrcWhileLocked.length > 0) {
    const src = pendingSoundSrcWhileLocked.shift();
    if (src) {
      soundRequestQueue.push(src);
    }
  }
  void runNotificationSoundDrain();
}

function tryUnlockAudioFromUserGesture() {
  if (audioUnlocked) {
    return;
  }
  try {
    const a = getSharedNotificationAudio();
    if (!a) {
      return;
    }
    a.src = SILENT_WAV;
    a.volume = 0.0001;
    void a.play().catch(() => {});
    audioUnlocked = true;
    flushPendingSoundsAfterUnlock();
  } catch (_) {
    audioUnlocked = false;
  }
}
function FcmForegroundNotifier({ user }) {
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const navigate = useNavigate();

  useEffect(() => {
    const onFirstGesture = () => {
      tryUnlockAudioFromUserGesture();
    };
    document.addEventListener("pointerdown", onFirstGesture, true);
    document.addEventListener("keydown", onFirstGesture, true);
    document.addEventListener("touchstart", onFirstGesture, true);
    return () => {
      document.removeEventListener("pointerdown", onFirstGesture, true);
      document.removeEventListener("keydown", onFirstGesture, true);
      document.removeEventListener("touchstart", onFirstGesture, true);
    };
  }, []);

  useEffect(() => {
    const handler = (event) => {
      try {
        const payload = event.detail;
        if (!payload) {
          return;
        }
        const isAdmin = user?.role?.name === "admin";
        if (
          isAdmin &&
          isOrderLikeForegroundFcmPayload(payload) &&
          !readAdminForegroundOrderNotificationsEnabled()
        ) {
          return;
        }
        const n = payload.notification || {};
        const data = payload.data || {};
        const title =
          String(n.title || data.title || "Kolay Doy").trim() || "Kolay Doy";
        const body = String(n.body || data.body || "").trim();
        const rawLink =
          payload.fcmOptions?.link ||
          data.link ||
          data.url ||
          payload.webpush?.fcm_options?.link;
        const key = `fcm-${payload.messageId || `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`}`;

        const onClick = () => {
          tryUnlockAudioFromUserGesture();
          if (rawLink) {
            const path =
              String(rawLink).trim().startsWith("/") ?
                String(rawLink).trim()
              : `/${String(rawLink).trim()}`;
            navigate(path);
          }
          closeSnackbar(key);
        };

        enqueueSnackbar(
          <Box
            role={rawLink ? "button" : undefined}
            onClick={rawLink ? onClick : undefined}
            sx={{ cursor: rawLink ? "pointer" : "default", maxWidth: 360 }}
          >
            <Typography component="div" variant="subtitle2" fontWeight={700}>
              {title}
            </Typography>
            {body ?
              <Typography
                component="div"
                variant="body2"
                color="text.secondary"
                sx={{ mt: 0.5 }}
              >
                {body}
              </Typography>
            : null}
          </Box>,
          {
            key,
            variant: "info",
            anchorOrigin: { vertical: "bottom", horizontal: "right" },
            autoHideDuration: 9000,
          },
        );

        try {
          enqueueNotificationSoundPlayback(payload);
        } catch (soundErr) {
          console.error("FCM notification sound enqueue failed", soundErr);
        }
      } catch (e) {
        console.error("FcmForegroundNotifier handler failed", e);
      }
    };

    window.addEventListener("fcm-foreground-message", handler);
    return () => window.removeEventListener("fcm-foreground-message", handler);
  }, [enqueueSnackbar, closeSnackbar, navigate, user]);

  return null;
}

const mstp = (state) => ({ user: state.user });

export default connect(mstp)(FcmForegroundNotifier);
